// My (MUI, /my). Presentation port of MyPage — same data (stats, recent, pending,
// morning brief), rebuilt with mui/* primitives + brainTokens.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Box, Button, Skeleton } from "@mui/material";
import { browseRecent, getStats, morningBrief, reviewPending } from "../../lib/brain-api";
import type { BrainStats, PendingItem, Thought } from "../../lib/brain-types";
import { useProfile } from "../../lib/profile";
import { brainTokens } from "../../design/brainTheme";
import { PageHeader, SectionCard, StatusPill, Icon } from "../../mui";

const c = brainTokens.color;

interface MyPageMuiProps {
  embeddedTitle?: string;
}

export function MyPageMui({ embeddedTitle = "My" }: MyPageMuiProps) {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const [stats, setStats] = useState<BrainStats | null>(null);
  const [recent, setRecent] = useState<Thought[]>([]);
  const [pending, setPending] = useState<PendingItem[]>([]);
  const [brief, setBrief] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [statsResult, recentResult, pendingResult, briefResult] = await Promise.allSettled([
          getStats(),
          browseRecent({ scope: "personal", limit: 8 }),
          reviewPending(undefined, 20),
          morningBrief(),
        ]);
        if (cancelled) return;
        if (statsResult.status === "fulfilled") setStats(statsResult.value);
        if (recentResult.status === "fulfilled") setRecent(recentResult.value);
        if (pendingResult.status === "fulfilled") setPending(pendingResult.value);
        if (briefResult.status === "fulfilled") setBrief(briefResult.value);
        const rejected = [statsResult, recentResult, pendingResult].find((result) => result.status === "rejected");
        if (rejected?.status === "rejected") {
          setError(rejected.reason instanceof Error ? rejected.reason.message : "Failed to load personal dashboard data.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const meta = profile?.profileMissing
    ? "No /me profile yet; access is claim-derived and personal-first."
    : profile?.displayName;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <PageHeader
        title={embeddedTitle}
        eyebrow={
          <>
            Personal operating context
            {meta && (
              <>
                {" · "}
                {meta}
              </>
            )}
          </>
        }
        actions={
          <Button variant="contained" startIcon={<Icon name="search" size={15} />} onClick={() => navigate("/knowledge?scope=personal")}>
            Search
          </Button>
        }
      />

      {error && (
        <Box
          sx={{
            fontSize: 13,
            color: c.warnText,
            bgcolor: c.warnBg,
            border: `1px solid ${c.warnBorder}`,
            borderRadius: `${brainTokens.radius.panel}px`,
            px: 2,
            py: 1.25,
          }}
        >
          {error}
        </Box>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        <SectionCard sx={{ minHeight: 220 }}>
          <Box sx={{ fontSize: 15, fontWeight: 700, color: c.ink, mb: 2 }}>Morning brief</Box>
          {loading ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Skeleton variant="text" width="90%" />
              <Skeleton variant="text" width="75%" />
              <Skeleton variant="text" width="80%" />
            </Box>
          ) : brief ? (
            <Box sx={{ color: c.ink, fontSize: 14, "& p": { m: 0, mb: 1 } }}>
              <ReactMarkdown>{brief}</ReactMarkdown>
            </Box>
          ) : (
            <Box sx={{ color: c.muted2, fontSize: 13 }}>
              The morning_brief tool did not return content. This widget is ready for Phase 3 data.
            </Box>
          )}
        </SectionCard>

        <SectionCard sx={{ minHeight: 220 }}>
          <Box sx={{ fontSize: 15, fontWeight: 700, color: c.ink }}>My memories</Box>
          {loading ? (
            <Skeleton variant="text" width={120} height={48} sx={{ mt: 1 }} />
          ) : (
            <Box sx={{ fontSize: 24, fontWeight: 600, color: c.ink, mt: 1 }}>{stats?.total ?? "-"}</Box>
          )}
          <Box sx={{ color: c.muted2, fontSize: 13, mt: 1, mb: 2 }}>
            Existing stats are global today; scoped counts need GET /admin/overview or a scoped stats endpoint.
          </Box>
          <Button variant="outlined" startIcon={<Icon name="search" size={15} />} onClick={() => navigate("/knowledge?scope=personal")}>
            Open personal search
          </Button>
        </SectionCard>

        <SectionCard>
          <Box sx={{ fontSize: 15, fontWeight: 700, color: c.ink, mb: 2 }}>Recent personal captures</Box>
          {loading ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Skeleton variant="text" />
              <Skeleton variant="text" />
              <Skeleton variant="text" />
            </Box>
          ) : recent.length === 0 ? (
            <Box sx={{ color: c.muted2, fontSize: 13 }}>No recent personal memories found.</Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {recent.map((thought, index) => (
                <Box key={thought.id ?? `${thought.created_at}-${index}`} sx={{ borderBottom: `1px solid ${c.border}`, pb: 1.5 }}>
                  <Box sx={{ color: c.ink, fontSize: 14 }}>{thought.content.slice(0, 180)}</Box>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mt: 1 }}>
                    {thought.topics.slice(0, 4).map((topic) => (
                      <StatusPill key={topic} tone="neutral" label={topic} />
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </SectionCard>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <SectionCard>
            <Box sx={{ fontSize: 15, fontWeight: 700, color: c.ink }}>My pending actions</Box>
            <Box sx={{ fontSize: 24, fontWeight: 600, color: c.ink, mt: 1 }}>{loading ? "-" : pending.length}</Box>
            <Box sx={{ color: c.muted2, fontSize: 13, mt: 1, mb: 2 }}>
              Review items currently available from review_pending.
            </Box>
            <Button variant="outlined" onClick={() => navigate("/review")}>
              Open review
            </Button>
          </SectionCard>

          <SectionCard>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 1 }}>
              <Box sx={{ fontSize: 15, fontWeight: 500, color: c.ink }}>Recently Used</Box>
              <StatusPill tone="neutral" label="Needs Phase 6" />
            </Box>
            <Box sx={{ fontSize: 13, color: c.muted2 }}>
              Retrieval history and saved useful items need GET /me/recent-retrievals.
            </Box>
          </SectionCard>
        </Box>
      </Box>
    </Box>
  );
}
